import rootStore from "@/app/store";
import crud, { ApiResponse } from "../crud";
import { OfficeLocation } from "../locations";

export interface LandlordUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  telegram: number;
}

export interface BusinessProfile {
  id: number;
  display_name: string;
  inn: string;
  ogrn: string;
  legal_address: string;
  copy_email?: string;
  chatpush_token?: string;
}

export interface BigProfileCreation {
  business_display_name: string;
  location_display_name: string;
  location_total_area: number;
  inn: string;
  ogrn: string;
  address: string;
  city: string;
  metro_station: string;
  district: string;
  req_display_name: string;
  kpp: string;
  bank_account: string;
  bank_name: string;
  bik: string;
  correspondent_account: string;
  phone: string;
  email: string;
  req_owner_name: string;
}

export type BusinessProfileCreate = Omit<BusinessProfile, "id">;
export type BusinessProfileUpdate = Partial<BusinessProfileCreate>;

export function BusinessProfilesAPI(baseUrl: string) {
  return {
    list: async () => {
      return (await crud.get(
        `${baseUrl}/business-profiles/`,
        undefined,
        true,
      )) as ApiResponse<BusinessProfile[]>;
    },
    get: async (id: number) => {
      return (await crud.get(
        `${baseUrl}/business-profiles/${id}`,
        undefined,
        true,
      )) as ApiResponse<BusinessProfile>;
    },
    create: async (profileData: BusinessProfileCreate) => {
      return (await crud.post(
        `${baseUrl}/business-profiles/`,
        profileData,
        true,
      )) as ApiResponse<BusinessProfile>;
    },
    update: async (id: number, profileData: BusinessProfileUpdate) => {
      return (await crud.put(
        `${baseUrl}/business-profiles/${id}`,
        profileData,
        true,
      )) as ApiResponse<{}>;
    },
    delete: async (id: number) => {
      return (await crud.delete(
        `${baseUrl}/business-profiles/${id}`,
        true,
      )) as ApiResponse<{}>;
    },
    getLocations: async (id: number) => {
      return (await crud.get(
        `${baseUrl}/business-profiles/${id}/locations`,
        undefined,
        true,
      )) as ApiResponse<OfficeLocation[]>;
    },
    generateInviteLink: async () => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/business-profiles/generate-invite-link/?business_profile_id=${businessProfileId}`,
        null,
        true,
      )) as { result: string; invite_hash: string };
    },
    getLandlords: async () => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/business-profiles/${businessProfileId}/landlords/?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<LandlordUser[]>;
    },
    transferOwnership: async (username: string) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/business-profiles/${businessProfileId}/transfer/?business_profile_id=${businessProfileId}&new_username=${username}`,
        {},
        true,
      )) as ApiResponse<boolean>;
    },
    getBigProfileProps: async (code: string) => {
      return (await crud.get(
        `${baseUrl}/business-profiles/invitation/${code}`,
        undefined,
        true,
      )) as ApiResponse<BigProfileCreation>;
    },
    createBigProfile: async (
      profileData: BigProfileCreation,
      code?: string,
    ) => {
      return (await crud.post(
        `${baseUrl}/business-profiles/invitation/${code ? code : "no-code"}/submit`,
        profileData,
        true,
      )) as ApiResponse<BusinessProfile>;
    },
  };
}
